import { kluczKraju } from '../../kraj'

/**
 * Trzecia droga asystenta doboru: „Projektuję na modelu 3D” (prośba właściciela 2026-10-12). Z asystenta idziemy do
 * Projektu 3D (`/projekt-3d?asystent=1`): tam ktoś znajduje dom na mapie, układa moduły i sprawdza, ile się mieści.
 * „Zapisz i wróć do asystenta” zapisuje wynik modelu w pamięci sesji i otwiera asystenta z `?z3d=1`, który wypełnia
 * formularz: liczbę i moc modułów (tryb „liczba modułów”, kWp liczy się samo), magazyn, rodzaj konstrukcji
 * z pokrycia dachu i województwo do Projektu (opłacalność). Sam projekt zostaje w pamięci Projektu 3D, więc
 * „Wróć do modelu” otwiera go w tym samym miejscu.
 */

const KLUCZ = kluczKraju('vv:z-projektu-3d')

/** Pokrycie dachu z Projektu 3D (`POKRYCIA`) na rodzaj konstrukcji asystenta (`lib/konstrukcja` `KONSTRUKCJE`). */
const POKRYCIE_NA_KONSTRUKCJE = {
  dachowka_ceramiczna: 'dachowka_ceramiczna',
  dachowka_betonowa: 'dachowka_ceramiczna', // ten sam montaż na hakach co ceramiczna
  dachowka_karpiowka: 'dachowka_karpiowka',
  blachodachowka: 'blachodachowka',
  rabek: 'rabek',
  blacha_trapezowa: 'blacha_trapezowa',
  dachowka_bitumiczna: 'dachowka_bitumiczna',
  membrana: 'membrana_papa',
}

export const konstrukcjaZProjektu = ({ grunt = false, plaski = false, pokrycie = '' } = {}) => {
  if (grunt) return 'grunt'
  if (plaski) return 'balastowy_plaski'
  return POKRYCIE_NA_KONSTRUKCJE[pokrycie] || null
}

/** Zestaw danych z modelu dla asystenta; null, gdy na dachu nie ma jeszcze modułów. */
export const daneDlaAsystenta = ({ projekt, modul, liczbaModulow, wojewodztwo = null, miejsce = '', rokKwh = null }) => {
  if (!(liczbaModulow > 0) || !(modul?.moc > 0)) return null
  const grunt = projekt?.rodzaj === 'grunt'
  const plaski = !grunt && projekt?.budynek?.skrzydla?.[0]?.dach?.typ === 'plaski'
  const f = projekt?.falownik?.model
  return {
    liczbaModulow,
    mocModuluW: modul.moc,
    kwp: Math.round((liczbaModulow * modul.moc) / 10) / 100,
    modul: [modul.producent, modul.nazwa].filter(Boolean).join(' ') || null,
    magazynKwh: projekt?.magazyn?.pojemnosc > 0 ? projekt.magazyn.pojemnosc : null,
    falownikKw: f?.mocKw ?? null,
    konstrukcja: konstrukcjaZProjektu({ grunt, plaski, pokrycie: projekt?.wyglad?.pokrycie?.typ }),
    wojewodztwo: wojewodztwo || null,
    miejsce: String(miejsce || ''),
    rokKwh: rokKwh ? Math.round(rokKwh) : null,
  }
}

export const zapiszDlaAsystenta = (dane) => {
  try { sessionStorage.setItem(KLUCZ, JSON.stringify(dane)) } catch { /* prywatne okno: asystent otworzy się bez danych */ }
}

export const odczytajZProjektu3D = () => {
  try { return JSON.parse(sessionStorage.getItem(KLUCZ)) || null } catch { return null }
}

export const zapomnijProjekt3D = () => {
  try { sessionStorage.removeItem(KLUCZ) } catch { /* nic */ }
}

/** `?asystent=1` w adresie Projektu 3D: projekt otwarty z asystenta doboru. */
export const zAsystenta = (search) => new URLSearchParams(search).get('asystent') === '1'

/** Adres Projektu 3D otwieranego z asystenta. */
export const ADRES_PROJEKTU_Z_ASYSTENTA = '/projekt-3d?asystent=1'

/** Adres powrotu do asystenta z danymi modelu. */
export const ADRES_POWROTU_DO_ASYSTENTA = '/dobor-zestawu-pv?z3d=1'

/** Czy asystent ma wczytać dane modelu (`?z3d=1`), i ten sam adres bez parametru (po wczytaniu). */
export const wczytacZ3D = (search) => new URLSearchParams(search).get('z3d') === '1'
export const adresBezZ3D = (pathname, search) => {
  const q = new URLSearchParams(search)
  q.delete('z3d')
  const reszta = q.toString()
  return `${pathname}${reszta ? `?${reszta}` : ''}`
}

/**
 * Moduły z mocą znaną wcześniej (A4, 2026-10-24: np. z prostego doboru albo wpisaną przed mapą): po zbudowaniu modelu
 * dach nie wypełnia się cały, tylko tryb „liczba albo moc” bierze tę moc. Tryb liczby ustawiony przez użytkownika zostaje.
 */
export const zZnanaMoca = (moduly, kwp, zDoboru = null) => {
  if (!(Number(kwp) > 0) || moduly?.tryb === 'liczba') return moduly
  return { ...moduly, tryb: 'liczba', naPolaci: {}, cel: celZMocy(moduly?.cel, kwp, zDoboru) }
}

/**
 * Cel „według mocy” z mocy znanej z doboru. `zDoboru` ({ czas, grunt }) zostaje w celu jako `cel.zDoboru`
 * (decyzja właściciela 2026-10-25): krok Moduły pokazuje wtedy źródło i datę mocy, dopóki ktoś jej nie zmieni.
 */
const celZMocy = (cel, kwp, zDoboru) => {
  const moc = Math.round(Number(kwp) * 10) / 10
  const reszta = { ...(cel || {}) }
  delete reszta.zDoboru
  return { ...reszta, wedlug: 'moc', kwp: moc, ...(zDoboru?.czas ? { zDoboru: { czas: zDoboru.czas, kwp: moc, grunt: Boolean(zDoboru.grunt) } } : {}) }
}

/** Moc z doboru, która nadal rządzi modułami (`{ czas, kwp, grunt }`), albo null, gdy ktoś zmienił moc, tryb albo liczby. */
export const mocZDoboru = (moduly, tryb = moduly?.tryb) => {
  const c = moduly?.cel
  const z = c?.zDoboru
  if (!z || tryb !== 'liczba' || c.wedlug !== 'moc' || c.kwp !== z.kwp) return null
  return Object.keys(moduly.naPolaci || {}).length ? null : z
}

const dwieCyfry = (n) => String(n).padStart(2, '0')
/** Data zapisu doboru do dopisku („24.10”). */
export const dzienMiesiaca = (ms) => { const d = new Date(ms); return `${dwieCyfry(d.getDate())}.${dwieCyfry(d.getMonth() + 1)}` }

/** Konstrukcja na gruncie ma zawsze tryb „liczba albo moc”, więc przy pierwszym obszarze bierze znaną moc wprost. */
export const zZnanaMocaNaGruncie = (moduly, kwp, zDoboru = null) => {
  if (!(Number(kwp) > 0)) return moduly
  return { ...moduly, cel: celZMocy(moduly?.cel, kwp, zDoboru) }
}

/**
 * Rodzaj instalacji z odpowiedzi doboru (uwaga G1, 2026-10-25): „na gruncie” daje konstrukcję wolnostojącą.
 * - `rodzajZDoboru(wybor)`: zapisany wybór prostego doboru (`lib/doborCele` `wyborDoboru`, pole `dach: 'Grunt'`);
 * - `rodzajZOdpowiedzi(o)`: odpowiedzi asystenta (`montaz` 'grunt' albo 'dach' w prostym doborze i asystencie modułów).
 */
export const rodzajZDoboru = (wybor) => (wybor?.dach === 'Grunt' ? 'grunt' : null)
export const rodzajZOdpowiedzi = (o) => (o?.montaz === 'grunt' || o?.montaz === 'dach' ? o.montaz : null)
